import { Injectable } from '@angular/core';
import {
  IOccupationCard,
  OccupationCategory
} from '@cpd-app/core-module/models';
import {
  GetOccupationCardsGQL,
  OccupationCardCategoryType
} from '@cpd-app/generated';
import { catchError, filter, map, Observable, of } from 'rxjs';
import {
  alignmentRank,
  bandScore,
  compareByFit,
  MatchBands,
  overqualifiedCount
} from '@cpd-app/core-module/models/survey/match-band.util';
import {
  addOccupationToMap,
  initializeOccupationCardCategoryMap
} from './occupation-category.config';

@Injectable({
  providedIn: 'root'
})
export class OccupationCardsService {
  constructor(private getOccupationCardsGQL: GetOccupationCardsGQL) {}

  /**
   * `state` is a two-letter abbreviation, PWS 4.1.12. When given, each card's
   * medianAnnualWage is that state's median where one exists, and the card's
   * wageState says which state the figure is for.
   */
  /**
   * `learnerEducationLevel` is the member's highest COMPLETED education as a
   * ladder index, or null when nothing is on file.
   *
   * It is a parameter rather than a dependency because this is a plain query
   * service and the effect that calls it already holds the profile. Passing
   * nothing is a safe default: `compareByFit` then orders exactly as it did
   * before, which is what a caller who cannot know the member should get.
   *
   * This is what the home page's "Best fit for you" ends up using. That branch
   * does no sorting of its own -- it keeps the order the cards arrive in, which
   * is built here -- so an education term missing here is an education term
   * missing from the first list a member sees.
   */
  public getOccupationCardsByCategory(
    state?: string,
    fetchPolicy?: 'cache-first' | 'network-only',
    learnerEducationLevel: number | null = null
  ): Observable<{
    categories: OccupationCategory[];
    occupationCards: IOccupationCard[];
  }> {
    return this.getOccupationCardsGQL
      .fetch(
        { state: state ?? null },
        fetchPolicy ? { fetchPolicy } : undefined
      )
      .pipe(
        // Apollo deduplicates identical in-flight queries and hands one of two
        // subscribers a frame with `data` undefined. Mapping that frame emitted
        // an empty list to whichever consumer got it, which is how the match
        // list on Home showed nothing while the store held six cards. Dropping
        // the frame makes that subscriber wait for the real one instead.
        filter((result) => !!result?.data),
        catchError(() => {
          return of({ data: { getOccupationCards: [] } });
        }),
        map((result) => {
          const occupationCategoriesMap = initializeOccupationCardCategoryMap();
          const occupationCardsMap = new Map<string, IOccupationCard>();

          // `result.data` can be undefined, not just `result`: apollo-angular
          // emits a frame with data undefined while a query is in flight, and
          // this used to read result.data.getOccupationCards straight through
          // and throw a TypeError on it. Two consumers of this query on one page
          // is enough to hit that frame, which is how it surfaced: the match list
          // on Home received zero cards while the store received six.
          const cards = result?.data?.getOccupationCards;

          if (!cards?.length) {
            return {
              occupationCards: [],
              categories: Array.from(occupationCategoriesMap.values())
            };
          }

          // Process cards in a single loop
          cards.forEach((item: any) => {
            /*
             * THIS SUM IS THE ORDER THE WHOLE APP SEES.
             *
             * It added the four band ids raw, and `api.MatchQuality` numbers
             * Overqualified 6, one above Excellent. So the sort below put every
             * role the learner has already outgrown at the very top, and it did
             * it on the first screen they land on, because the home page's
             * "Best fit for you" does no sorting of its own -- it keeps this
             * order. Measured on dev, user 70956: Veterinary Technologists,
             * Sales Representatives and General and Operations Managers, all
             * band 6, sat above Anesthesiologist Assistants, Logisticians and
             * Software Developers, all a genuine band 5.
             *
             * `alignmentRank` is the same rule the quick-match list uses, so
             * the two screens can no longer disagree about what "best fit"
             * means. Overqualified contributes nothing rather than the most.
             */
            const matchTotal = alignmentRank(item);

            const card: IOccupationCard = {
              ...item,
              matchTotal
            };

            // Deduplicate cards by onetCode
            if (!occupationCardsMap.has(card.onetCode)) {
              occupationCardsMap.set(card.onetCode, card);
            }

            // Add card to categories map
            addOccupationToMap(occupationCategoriesMap, card);
          });

          /*
           * Convert Map to Array and sort unique cards.
           *
           * THIS ORDER IS WHAT THE HOME PAGE SHOWS. Its "Best fit for you"
           * branch returns the rows untouched, so whatever is missing here is
           * missing from the first list a member sees.
           *
           * Three separate keys, not points taken off one score: overqualified,
           * then how far below the learner's education the role sits, then the
           * four bands. Mixing them lets a strong enough showing on interests,
           * values and lifestyle float a role the learner is plainly past back
           * to the top, which is the complaint this answers. Nothing is
           * dropped; such a role still appears, below work that fits.
           */
          const uniqueOccupationCards = Array.from(occupationCardsMap.values());
          uniqueOccupationCards.sort((a, b) =>
            compareByFit(a, b, learnerEducationLevel)
          );

          // Sort occupations within categories
          const occupationCategories = Array.from(
            occupationCategoriesMap.values()
          );
          occupationCategories.forEach((category) => {
            const sortKey = this.getSortKeyForCategory(
              category.occupationCardCategoryType
            );

            /*
             * The same band rule inside a carousel as down the main list.
             *
             * Five of the seven categories sort on a `*MatchQualityId` column,
             * so sorting those as raw numbers put Overqualified first here too,
             * on a row headed by the dimension it is not even a grade for.
             *
             * `bandScore` is applied ONLY to those columns. `matchTotal` is a
             * sum that is already banded, and a legitimate one can be exactly
             * 6 (Excellent plus Poor, say) -- putting it through `bandScore`
             * would read that 6 as the overqualified flag and zero it.
             */
            const isBand = sortKey !== 'matchTotal';
            // `MatchBands` rather than a card type: a category holds the
            // generated `OccupationCard`, the main list holds `IOccupationCard`,
            // and the band fields are all this needs from either.
            const rank = (card: MatchBands): number => {
              const raw = Number((card as Record<string, unknown>)[sortKey]);

              return isBand ? bandScore(raw) : raw;
            };

            category.occupations.sort((a, b) => {
              const overqualified =
                overqualifiedCount(a) - overqualifiedCount(b);

              return overqualified !== 0
                ? overqualified
                : rank(b) - rank(a);
            });
          });

          return {
            occupationCards: uniqueOccupationCards,
            categories: occupationCategories
          };
        })
      );
  }

  private getSortKeyForCategory(
    categoryType: OccupationCardCategoryType
  ): string {
    switch (categoryType) {
      case OccupationCardCategoryType.Lifestyle:
      case OccupationCardCategoryType.HotLocations:
        return 'lifestyleMatchQualityId';
      case OccupationCardCategoryType.Values:
        return 'valuesMatchQualityId';
      case OccupationCardCategoryType.Interests:
        return 'interestsMatchQualityId';
      case OccupationCardCategoryType.Competencies:
      case OccupationCardCategoryType.AiFastestPath:
        return 'skillsMatchQualityId';
      default:
        return 'matchTotal';
    }
  }
}
